import { useEffect, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, MailCheck, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useBranding } from "../lib/branding";

export default function SuperadminLogin() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [step, setStep] = useState("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  // MFA state
  const [challengeId, setChallengeId] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [resendCooldown, setResendCooldown] = useState(0);
  const codeRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/setup/status");
        if (!cancelled && data.needs_setup) navigate("/setup", { replace: true });
      } catch { /* swallow */ }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  useEffect(() => {
    if (step === "verify") setTimeout(() => codeRef.current?.focus(), 50);
  }, [step]);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  if (localStorage.getItem("pragma_super_token")) {
    return <Navigate to="/superadmin" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/superadmin/auth/login", { email: email.trim(), password });
      if (data && data.mfa_required) {
        setChallengeId(data.challenge_id);
        setEmailHint(data.email_hint || "your inbox");
        setResendCooldown(30);
        setStep("verify");
      } else if (data && data.token) {
        localStorage.setItem("pragma_super_token", data.token);
        navigate("/superadmin");
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Login failed");
    }
    setLoading(false);
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\d{6}$/.test(code)) { setErr("Enter the 6-digit code from your email"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", {
        challenge_id: challengeId, code, remember_device: rememberDevice,
      });
      localStorage.setItem("pragma_super_token", data.token);
      navigate("/superadmin");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
    }
    setLoading(false);
  };

  const resend = async () => {
    setErr("");
    try {
      await api.post("/auth/mfa/resend", { challenge_id: challengeId });
      setResendCooldown(30);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not resend");
    }
  };

  return (
    <div className="auth-split" data-testid="superadmin-login-shell">
      {/* ─── Left (white) — form ─── */}
      <div className="auth-split-left">
        <div className="auth-split-logo">
          <img
            src={branding.logo_data_url || "/pragma-login-logo-v4.png"}
            alt="Pragma"
            data-testid="superadmin-brand-logo"
          />
        </div>

        <div className="auth-split-form-wrap">
          <span className="auth-card-badge" data-testid="superadmin-badge" style={{ marginBottom: 14 }}>SUPERADMIN</span>
          {step === "credentials" ? (
            <>
              <h1 className="auth-split-h1" data-testid="superadmin-heading">Pragma Operations</h1>
              <p className="auth-split-sub">Platform-level access. Tenant data is gated.</p>
              <form onSubmit={submit} data-testid="superadmin-login-form">
                <input id="sa-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                       className="auth-split-input" placeholder="Email address" data-testid="superadmin-email"
                       required autoComplete="email" aria-label="Email address" />
                <input id="sa-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       className="auth-split-input" placeholder="Password" data-testid="superadmin-password"
                       required autoComplete="current-password" aria-label="Password" />
                {err && <div className="auth-split-error" data-testid="superadmin-error">{err}</div>}
                <div className="auth-split-actions" style={{ justifyContent: "flex-end" }}>
                  <button type="submit" className="auth-split-submit" disabled={loading} data-testid="superadmin-submit">
                    {loading ? "Signing in…" : "Enter portal"}
                    <ArrowRight strokeWidth={2.2} />
                  </button>
                </div>
              </form>
            </>
          ) : (
            <>
              <h1 className="auth-split-h1" data-testid="superadmin-mfa-heading">Verify it's you</h1>
              <p className="auth-split-sub">
                <MailCheck size={14} style={{ display: "inline", verticalAlign: -2, marginRight: 6, color: "var(--pg-accent-deep)" }} />
                We sent a 6-digit code to <strong>{emailHint}</strong>. It expires in 10 minutes.
              </p>
              <form onSubmit={submitCode} data-testid="superadmin-mfa-form">
                <input ref={codeRef} type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                       value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       className="auth-split-input" placeholder="000000" data-testid="superadmin-mfa-code"
                       style={{ fontFamily: "'JetBrains Mono', ui-monospace, Menlo, monospace",
                                letterSpacing: "0.36em", textAlign: "center", fontSize: 22 }}
                       aria-label="6-digit verification code" required />
                <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, marginBottom: 8,
                                fontSize: 13, color: "var(--pg-text-mute)", cursor: "pointer" }}>
                  <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)}
                         data-testid="superadmin-mfa-remember" />
                  Trust this device for 14 days
                </label>
                {err && <div className="auth-split-error" data-testid="superadmin-mfa-error">{err}</div>}
                <div className="auth-split-actions">
                  <button type="button" className="auth-split-secondary"
                          onClick={() => { setStep("credentials"); setCode(""); setErr(""); }}
                          data-testid="superadmin-mfa-back">
                    <ArrowLeft size={14} style={{ display: "inline", verticalAlign: -2, marginRight: 4 }} /> Back
                  </button>
                  <button type="submit" className="auth-split-submit"
                          disabled={loading || code.length !== 6} data-testid="superadmin-mfa-submit">
                    {loading ? "Verifying…" : "Verify & enter"}
                    <ArrowRight strokeWidth={2.2} />
                  </button>
                </div>
                <div style={{ marginTop: 16, fontSize: 13, color: "var(--pg-text-mute)" }}>
                  Didn't get it?{" "}
                  {resendCooldown > 0 ? (
                    <span>Resend in {resendCooldown}s</span>
                  ) : (
                    <button type="button" onClick={resend} className="auth-split-secondary"
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}
                            data-testid="superadmin-mfa-resend">
                      Resend code
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>

        <div className="auth-split-footer" data-testid="superadmin-tenant-link">
          Looking for your workspace? <a href="/login">Tenant sign in</a>
        </div>
      </div>

      {/* ─── Right (dark) — marketing copy ─── */}
      <div className="auth-split-right" data-testid="superadmin-marketing">
        <h2 className="auth-split-right-h1">
          Platform
          <br />
          <span className="accent">operations.</span>
        </h2>
        <p className="auth-split-right-sub">
          Manage tenants, branding and platform-level configuration.
          <br />
          Access is gated by superadmin credentials.
        </p>
        <ul className="auth-split-right-feats" data-testid="superadmin-feats">
          <li><ShieldCheck strokeWidth={2} /> Tenant management & impersonation</li>
          <li><ShieldCheck strokeWidth={2} /> Comp upgrades & trial extensions</li>
          <li><ShieldCheck strokeWidth={2} /> White-label branding controls</li>
        </ul>
      </div>
    </div>
  );
}
